function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function isGenericInternalMessage(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  return (
    normalized === "internal" ||
    normalized === "internal error" ||
    normalized === "functions/internal" ||
    normalized === "unknown error"
  );
}

function firstUsefulMessage(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const value = asString(candidate);
    if (!value) continue;
    if (isGenericInternalMessage(value)) continue;
    return value;
  }
  return "";
}

function normalizeCode(code: string): string {
  return code.replace(/^functions\//, "").trim().toLowerCase();
}

function fallbackForCode(code: string, fallback: string): string {
  if (code === "unauthenticated") return "Please sign in to continue.";
  if (code === "permission-denied") return "You do not have access to perform this action.";
  if (code === "failed-precondition") return "This action cannot run in the current state.";
  if (code === "not-found") return "The requested record was not found.";
  if (code === "invalid-argument") return "Some inputs are invalid. Please check and retry.";
  return fallback;
}

function asErrorPayload(error: unknown): {
  code: string;
  message: string;
  details: Record<string, unknown>;
} {
  const err = asRecord(error);
  const nested = asRecord(err.error);
  const customData = asRecord(err.customData);
  const detailsRaw = err.details ?? nested.details ?? customData.details;
  const details = {
    ...asRecord(customData.details),
    ...asRecord(nested.details),
    ...asRecord(err.details),
  };
  if (typeof detailsRaw === "string" && !asString(details.message)) {
    details.message = detailsRaw.trim();
  }
  const rawCode = asString(err.code || nested.code || nested.status || customData.code);
  const rawMessage = asString(
    err.message || nested.message || nested.error || customData.message || details.message
  );
  return { code: rawCode, message: rawMessage, details };
}

export function getFunctionErrorMessage(error: unknown, fallback: string): string {
  const payload = asErrorPayload(error);
  const rawCode = payload.code;
  const code = rawCode ? normalizeCode(rawCode) : "";
  const rawMessage = isGenericInternalMessage(payload.message) ? "" : payload.message;
  const details = payload.details;
  const reason = asString(details.reason);
  const detailMessage = firstUsefulMessage(
    details.userMessage,
    details.message,
    details.errorMessage,
    details.error
  );
  const errorId = asString(details.errorId);
  const sourceCode = asString(details.sourceCode || details.code);
  const failedStep = asString(details.failedStep || details.step);
  const codeFallback = fallbackForCode(code, fallback);

  const base = firstUsefulMessage(rawMessage, detailMessage, reason, codeFallback, fallback);
  const extra: string[] = [];
  if (
    code &&
    ["unauthenticated", "permission-denied", "failed-precondition", "invalid-argument"].includes(code) &&
    !base.toLowerCase().includes(code)
  ) {
    extra.push(`code: ${code}`);
  }
  if (failedStep) extra.push(`step: ${failedStep}`);
  if (sourceCode) extra.push(`cause: ${sourceCode}`);
  if (errorId) extra.push(`ref: ${errorId}`);

  if (!extra.length) return base;
  return `${base} (${extra.join(" • ")})`;
}

// Backward-compatible alias used across pages/components.
export function formatFunctionActionError(error: unknown, fallback: string): string {
  return getFunctionErrorMessage(error, fallback);
}
